import React, { useEffect, useRef, useState } from "react";
import { TodoList } from "@/api/types";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import * as Dialog from "@radix-ui/react-dialog";
import { deleteStateEntry, listsState, setStateEntry, uiState } from "@/states.ts";
import { deleteList, updateList } from "@/api/offline.ts";
import { processQueue } from "@/api/queue.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { ColorPicker } from "@/components/ColorPicker.tsx";
import { useCurrentList } from "@/hooks/lists.ts";
import { useLocation } from "wouter";

export interface EditListDialogProps {
	list: TodoList;
}

const EditListDialog: React.FC<EditListDialogProps> = (props) => {
	const [title, setTitle] = useState(props.list.title);
	const [color, setColor] = useState(props.list.color ?? "");
	const currentList = useCurrentList(false);
	const isMobile = useIsMobile();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [location, setLocation] = useLocation();

	const handleUpdate = () => {
		const newList = { ...props.list, title, color };
		updateList(newList).then((l) => {
			setStateEntry(listsState.lists, l.list, "merge");
		});
		processQueue();
		uiState.editingList = null;
	};

	const handleDelete = () => {
		const list = uiState.editingList!;
		deleteList(list).then((l) => {
			deleteStateEntry(listsState, "lists", list);
			if (list.id === currentList?.id) {
				setLocation("/");
			}
		});
		processQueue();
		uiState.editingList = null;
	};

	const handleClose = () => {
		uiState.editingList = null;
	};

	useEffect(() => {
		if (!isMobile || !inputRef.current) {
			return;
		}
		inputRef.current?.scrollIntoView();
	}, [isMobile, inputRef]);

	return (
		<>
			<Dialog.Root open={true}>
				<Dialog.Portal>
					<Dialog.Overlay className="DialogOverlay" />
					<Dialog.Content className="DialogContent">
						<div className={"p-4 flex flex-col space-y-2"}>
							<Dialog.Title className="DialogTitle">
								Edit List
							</Dialog.Title>
							<div className={"flex items-center gap-4"}>
								<Input
									ref={inputRef}
									placeholder={"Title"}
									value={title}
									className={
										"bg-secondary/30"
									}
									onChange={(e) =>
										setTitle(
											e.target
												.value,
										)
									}
								/>
								<ColorPicker
									color={color}
									onChange={(c) =>
										setColor(c)
									}
								/>
							</div>
							<div
								className={
									"flex justify-between items-center pt-4"
								}
							>
								<div>
									<Button
										variant={
											"destructive"
										}
										onClick={
											handleDelete
										}
									>
										Delete
									</Button>
								</div>
								<div className="flex gap-4">
									<Button
										onClick={
											handleUpdate
										}
									>
										Update
									</Button>
									<Button
										variant={"ghost"}
										onClick={
											handleClose
										}
									>
										Cancel
									</Button>
								</div>
							</div>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</>
	);
};

export { EditListDialog };
